import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Avatar, Chip, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Divider, Stack, CircularProgress, Alert,
  LinearProgress, Button,
} from '@mui/material';
import {
  AccountBalance, CardMembership, TrendingUp, CheckCircle,
  Warning, School, CalendarToday, Person, Badge,
  Wc, Cake, HomeWork, ReceiptLong, Inbox,
  EmojiEvents, AssignmentTurnedIn, Payment as PaymentIcon,
} from '@mui/icons-material';
import { studentPortalAPI } from '../../services/api';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import StudentLayout from './StudentLayout';
import { fDateDdMmmYyyy } from '../../utils/time';
import AdhocPaymentDialog from '../common/AdhocPaymentDialog';

// ── Shared helpers ────────────────────────────────────────────────────────────
const fmt = (v) => `UGX ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const SectionTitle = ({ icon: Icon, title }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2}>
    <Icon sx={{ fontSize: 18, color: 'primary.main' }} />
    <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.72rem' }}>
      {title}
    </Typography>
  </Box>
);

const MetricCard = ({ icon: Icon, label, value, color = '#2e7d32', bg = '#f1f8f1', sub }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderRadius: 2,
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
    }}
  >
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={700} sx={{ color, mt: 0.25, lineHeight: 1.2, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            {value}
          </Typography>
          {sub && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{sub}</Typography>}
        </Box>
        <Box sx={{ bgcolor: bg, borderRadius: 2, p: 1, display: 'flex' }}>
          <Icon sx={{ fontSize: 22, color }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const EmptyState = ({ icon: Icon, message }) => (
  <Box display="flex" flexDirection="column" alignItems="center" py={5} gap={1.5}>
    <Icon sx={{ fontSize: 48, color: 'text.disabled' }} />
    <Typography variant="body2" color="text.secondary">{message}</Typography>
  </Box>
);

// ── Overview Page ─────────────────────────────────────────────────────────────
function OverviewPage({ student, token }) {
  const [reportCards, setReportCards] = useState([]);
  const [fees, setFees]               = useState(null);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    Promise.all([
      studentPortalAPI.getReportCards(token),
      studentPortalAPI.getFeeSummary(token),
    ]).then(([rcRes, feeRes]) => {
      setReportCards(rcRes.data);
      setFees(feeRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [token]);

  const latest   = reportCards[0];
  const paidPct  = fees ? Math.min((fees.total_paid / (fees.total_allocated || 1)) * 100, 100) : 0;
  const recentPayments = fees?.payments?.slice(0, 3) ?? [];

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;

  return (
    <Box>
      {/* Welcome banner */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2, mb: 3,
          bgcolor: '#f1f8f1',
          border: '1px solid #c8e6c9',
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="h6" fontWeight={700} color="primary.dark">
            Welcome back, {student.first_name} 👋
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {student.class_stream} &nbsp;·&nbsp; {student.school_name}
          </Typography>
        </CardContent>
      </Card>

      {/* Fee metric cards */}
      {fees && (
        <>
          <SectionTitle icon={AccountBalance} title="Fee Summary" />
          <Grid container spacing={2} mb={3}>
            {[
              { icon: ReceiptLong,  label: 'Total Billed', value: fmt(fees.total_allocated), color: '#1565c0', bg: '#e3f2fd' },
              { icon: CheckCircle,  label: 'Total Paid',   value: fmt(fees.total_paid),      color: '#2e7d32', bg: '#f1f8f1' },
              { icon: Warning,      label: 'Balance Due',  value: fmt(fees.balance),
                color: fees.balance > 0 ? '#c62828' : '#2e7d32',
                bg:    fees.balance > 0 ? '#ffebee' : '#f1f8f1',
                sub:   fees.balance > 0 ? 'Outstanding' : 'Fully paid',
              },
            ].map((m) => (
              <Grid size={{ lg: 4, md: 4, sm: 12, xs: 12 }} key={m.label}>
                <MetricCard {...m} />
              </Grid>
            ))}
          </Grid>

          {/* Payment progress */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" mb={0.75}>
                <Typography variant="caption" color="text.secondary">Payment Progress</Typography>
                <Typography variant="caption" fontWeight={700} color="primary.main">{paidPct.toFixed(0)}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={paidPct}
                sx={{ height: 8, borderRadius: 4, bgcolor: '#e8f5e9', '& .MuiLinearProgress-bar': { borderRadius: 4 } }}
              />
            </CardContent>
          </Card>
        </>
      )}

      <Grid container spacing={2}>
        {/* Academic Summary */}
        <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <SectionTitle icon={EmojiEvents} title="Academic Summary" />
              {!latest ? (
                <EmptyState icon={AssignmentTurnedIn} message="No report cards available yet." />
              ) : (
                <Grid container spacing={1.5}>
                  {[
                    { label: 'Academic Year', value: latest.academic_year, icon: CalendarToday },
                    { label: 'Term',          value: latest.term,          icon: School },
                    { label: 'Overall %',     value: latest.overall_percentage != null ? `${Number(latest.overall_percentage).toFixed(1)}%` : '—', icon: TrendingUp },
                    { label: 'Grade',         value: latest.overall_grade || '—', icon: EmojiEvents },
                    { label: 'Division',      value: latest.division || '—',      icon: CardMembership },
                    { label: 'Class Position',value: latest.class_position ? `${latest.class_position} / ${latest.class_total}` : '—', icon: AssignmentTurnedIn },
                  ].map(({ label, value, icon: Icon }) => (
                    <Grid item xs={6} key={label}>
                      <Box sx={{ bgcolor: '#fafafa', borderRadius: 1.5, p: 1.25, border: '1px solid #f0f0f0' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
                        <Typography variant="body2" fontWeight={700} mt={0.25}>{value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Chip
                      label={latest.is_published ? 'Published' : 'Pending'}
                      size="small"
                      color={latest.is_published ? 'success' : 'default'}
                      icon={latest.is_published ? <CheckCircle /> : <Warning />}
                    />
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <SectionTitle icon={TrendingUp} title="Recent Payments" />
              {recentPayments.length === 0 ? (
                <EmptyState icon={Inbox} message="No recent payment activity." />
              ) : (
                <Stack spacing={1}>
                  {recentPayments.map((p) => (
                    <Box
                      key={p.id}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 1.25, bgcolor: '#fafafa', borderRadius: 1.5, border: '1px solid #f0f0f0' }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{p.fee_group?.name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.payment_date ? fDateDdMmmYyyy(p.payment_date) : '—'}</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700} color="success.main">
                        + {fmt(p.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Report Cards Page ─────────────────────────────────────────────────────────
function ReportCardsPage({ token }) {
  const [cards, setCards]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    studentPortalAPI.getReportCards(token)
      .then((r) => setCards(r.data))
      .catch(() => setError('Failed to load report cards.'))
      .finally(() => setLoading(false));
  }, [token]);

  const getGradeColor = (pct) => {
    if (pct == null) return 'text.primary';
    if (pct >= 75) return '#2e7d32';
    if (pct >= 50) return '#f57c00';
    return '#c62828';
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" fontWeight={700}>Report Cards</Typography>
        <Typography variant="body2" color="text.secondary">Your academic performance records</Typography>
      </Box>

      {loading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
      {error   && <Alert severity="error">{error}</Alert>}

      {!loading && !error && cards.length === 0 && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <EmptyState icon={AssignmentTurnedIn} message="No report cards available yet. Check back after results are published." />
          </CardContent>
        </Card>
      )}

      {!loading && !error && cards.length > 0 && (
        <>
          {/* Summary metric cards */}
          <Grid container spacing={2} mb={3}>
            {[
              { icon: CardMembership, label: 'Total Terms', value: cards.length,                                                                                                                                                    color: '#1565c0', bg: '#e3f2fd' },
              { icon: TrendingUp,     label: 'Best Score',  value: `${Math.max(...cards.filter(c => c.overall_percentage != null).map(c => Number(c.overall_percentage))).toFixed(1)}%`, color: '#2e7d32', bg: '#f1f8f1' },
              { icon: EmojiEvents,    label: 'Published',   value: cards.filter(c => c.is_published).length,                                                                                                                        color: '#6a1b9a', bg: '#f3e5f5' },
            ].map((m) => (
              <Grid size={{ lg: 4, md: 4, sm: 12, xs: 12 }} key={m.label}>
                <MetricCard {...m} />
              </Grid>
            ))}
          </Grid>

          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#fafafa' }}>
                      {['Year', 'Term', 'Score', 'Grade', 'Division', 'Position', 'Status'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cards.map((c) => (
                      <TableRow key={c.id} hover sx={{ '&:hover': { bgcolor: '#f9fbe7' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{c.academic_year}</TableCell>
                        <TableCell>{c.term}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700} sx={{ color: getGradeColor(c.overall_percentage) }}>
                            {c.overall_percentage != null ? `${Number(c.overall_percentage).toFixed(1)}%` : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell><Chip label={c.overall_grade || '—'} size="small" variant="outlined" /></TableCell>
                        <TableCell>{c.division || '—'}</TableCell>
                        <TableCell>{c.class_position ? `${c.class_position} / ${c.class_total}` : '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={c.is_published ? 'Published' : 'Pending'}
                            size="small"
                            color={c.is_published ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

// ── Fees Page ─────────────────────────────────────────────────────────────────
function FeesPage({ student, token }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [payOpen, setPayOpen] = useState(false);

  const fetchFeeSummary = () => {
    studentPortalAPI.getFeeSummary(token)
      .then((r) => setData(r.data))
      .catch(() => setError('Failed to load fee information.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchFeeSummary(); }, [token]);

  const paidPct = data ? Math.min((data.total_paid / (data.total_allocated || 1)) * 100, 100) : 0;

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" fontWeight={700}>Fee Statement</Typography>
        <Typography variant="body2" color="text.secondary">Your billing and payment history</Typography>
      </Box>

      {loading && <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>}
      {error   && <Alert severity="error">{error}</Alert>}

      {!loading && !error && data && (
        <>
          {/* Metric cards */}
          <Grid container spacing={2} mb={3}>
            {[
              { icon: ReceiptLong, label: 'Total Billed', value: fmt(data.total_allocated), color: '#1565c0', bg: '#e3f2fd' },
              { icon: CheckCircle, label: 'Total Paid',   value: fmt(data.total_paid),      color: '#2e7d32', bg: '#f1f8f1' },
              { icon: Warning,     label: 'Balance Due',  value: fmt(data.balance),
                color: data.balance > 0 ? '#c62828' : '#2e7d32',
                bg:    data.balance > 0 ? '#ffebee' : '#f1f8f1',
                sub:   data.balance > 0 ? 'Outstanding' : 'Fully paid',
              },
            ].map((m) => (
              <Grid size={{ lg: 4, md: 4, sm: 12, xs: 12 }} key={m.label}>
                <MetricCard {...m} />
              </Grid>
            ))}
          </Grid>

          {data.balance > 0 && (
            <Box display="flex" justifyContent="flex-end" mb={3}>
              <Button variant="contained" startIcon={<PaymentIcon />} onClick={() => setPayOpen(true)}>
                Pay Now
              </Button>
            </Box>
          )}

          <AdhocPaymentDialog
            open={payOpen}
            onClose={() => setPayOpen(false)}
            payerName={student?.full_name}
            balance={data.balance}
            onSubmit={async (mode, phoneNumber, amount, reason) => {
              const { data: res } = await studentPortalAPI.payFees({ mode, phone_number: phoneNumber, amount, reason }, token);
              return res;
            }}
            onCheckStatus={async (reference) => {
              const { data: res } = await studentPortalAPI.checkFeePaymentStatus(reference, token);
              return res;
            }}
            onPaid={() => fetchFeeSummary()}
          />

          {/* Progress bar */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" mb={0.75}>
                <Typography variant="body2" fontWeight={600}>Payment Progress</Typography>
                <Typography variant="body2" fontWeight={700} color="primary.main">{paidPct.toFixed(0)}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={paidPct}
                sx={{ height: 10, borderRadius: 5, bgcolor: '#e8f5e9', '& .MuiLinearProgress-bar': { borderRadius: 5 } }}
              />
              <Box display="flex" justifyContent="space-between" mt={0.75}>
                <Typography variant="caption" color="text.secondary">Paid: {fmt(data.total_paid)}</Typography>
                <Typography variant="caption" color="text.secondary">Total: {fmt(data.total_allocated)}</Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Fee Breakdown */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <SectionTitle icon={ReceiptLong} title="Fee Breakdown" />
              {data.allocations?.length === 0 ? (
                <EmptyState icon={Inbox} message="No fee allocations found." />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        {['Fee Group', 'Term', 'Amount', 'Status'].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(data.allocations || []).map((a) => (
                        <TableRow key={a.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{a.fee_group?.name || '—'}</TableCell>
                          <TableCell>{a.term?.name || '—'}</TableCell>
                          <TableCell>{fmt(a.amount)}</TableCell>
                          <TableCell>
                            <Chip
                              label={a.status || 'Pending'}
                              size="small"
                              color={a.status === 'paid' ? 'success' : a.status === 'partial' ? 'warning' : 'error'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <SectionTitle icon={TrendingUp} title="Payment History" />
              {data.payments.length === 0 ? (
                <EmptyState icon={Inbox} message="No payments recorded yet." />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        {['Date', 'Fee Group', 'Amount', 'Method'].map((h) => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.4, color: 'text.secondary' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.payments.map((p) => (
                        <TableRow key={p.id} hover sx={{ '&:hover': { bgcolor: '#f9fbe7' } }}>
                          <TableCell>{p.payment_date ? fDateDdMmmYyyy(p.payment_date) : '—'}</TableCell>
                          <TableCell>{p.fee_group?.name || '—'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} color="success.main">
                              {fmt(p.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={p.payment_method || 'N/A'} size="small" variant="outlined" />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}

// ── Profile Page ──────────────────────────────────────────────────────────────
function ProfilePage({ token }) {
  const { student: ctxStudent, updateStudent } = useStudentAuth();
  const [student, setStudent] = useState(ctxStudent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    studentPortalAPI.getProfile(token)
      .then((r) => {
        setStudent(r.data);
        updateStudent(r.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  const personalFields = [
    { label: 'Student Number',  value: student?.registration_no },
    { label: 'LIN',             value: student?.lin },
    { label: 'Gender',          value: student?.gender ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1) : null },
    { label: 'Date of Birth',   value: student?.birthday ? fDateDdMmmYyyy(student.birthday) : null },
    { label: 'Admission Date',  value: student?.admission_date ? fDateDdMmmYyyy(student.admission_date) : null },
    { label: 'Address',         value: student?.address },
  ];

  const academicFields = [
    { label: 'Class',           value: student?.class_stream },
    { label: 'School',          value: student?.school_name },
  ];

  const guardianFields = [
    { label: 'Guardian Name',   value: student?.guardian_name },
    { label: 'Relationship',    value: student?.guardian_relationship },
    { label: 'Guardian Phone',  value: student?.guardian_phone },
    { label: 'Guardian Email',  value: student?.guardian_email },
  ];

  const FieldRow = ({ label, value }) => (
    <Box sx={{ p: 1.25, bgcolor: '#fafafa', borderRadius: 1.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Box>
  );

  if (loading) return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h6" fontWeight={700}>My Profile</Typography>
        <Typography variant="body2" color="text.secondary">Your personal and academic information</Typography>
      </Box>

      {/* Header card */}
      <Card
        variant="outlined"
        sx={{ borderRadius: 2, mb: 2, bgcolor: '#f1f8f1', border: '1px solid #c8e6c9' }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={student?.picture_url}
              sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 24, fontWeight: 700 }}
            >
              {student?.first_name?.[0]}{student?.last_name?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700} color="primary.dark">{student?.full_name}</Typography>
              <Typography variant="body2" color="text.secondary">{student?.registration_no}</Typography>
              {student?.class_stream && (
                <Chip label={student.class_stream} size="small" color="primary" sx={{ mt: 0.5 }} />
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Personal info */}
        <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <SectionTitle icon={Person} title="Personal Information" />
              <Stack spacing={1}>
                {personalFields.filter(f => f.value).map((f) => <FieldRow key={f.label} {...f} />)}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Academic + Guardian info */}
        <Grid size={{ lg: 6, md: 6, sm: 12, xs: 12 }}>
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <SectionTitle icon={School} title="Academic Information" />
                <Stack spacing={1}>
                  {academicFields.filter(f => f.value).map((f) => <FieldRow key={f.label} {...f} />)}
                </Stack>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <SectionTitle icon={Person} title="Guardian / Parent" />
                <Stack spacing={1}>
                  {guardianFields.filter(f => f.value).map((f) => <FieldRow key={f.label} {...f} />)}
                  {guardianFields.every(f => !f.value) && (
                    <EmptyState icon={Person} message="No guardian information on record." />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Dashboard root ────────────────────────────────────────────────────────────
const PAGE_MAP = {
  'student-overview':     (props) => <OverviewPage {...props} />,
  'student-report-cards': (props) => <ReportCardsPage {...props} />,
  'student-fees':         (props) => <FeesPage {...props} />,
  'student-profile':      (props) => <ProfilePage token={props.token} />,
};

export default function StudentDashboard() {
  const { student, token } = useStudentAuth();
  const [currentPage, setCurrentPage] = useState('student-overview');

  if (!student) return null;

  const PageComponent = PAGE_MAP[currentPage];

  return (
    <StudentLayout currentPage={currentPage} onSelectPage={setCurrentPage}>
      {PageComponent && <PageComponent student={student} token={token} />}
    </StudentLayout>
  );
}
